/* eslint no-alert: "off" */
// Dependency imports
import { action, observable } from 'mobx';
import { useStaticRendering } from 'mobx-react';
import _ from 'lodash';
import jwtDecode from 'jwt-decode';

// Classes
import Config from '../../config';
import ApiCore from '../ApiCore';

// Helpers
import { orderTotal } from '../../helpers/cart';

// Server rendering
const isServer = !process.browser;
useStaticRendering(isServer);

const OrderKey = 'tv_order';

class CartStore {
  // Observables
  @observable order = [];

  @observable shipping = {};

  @observable coupon = {};

  @observable addToCartSuccess = false;

  /**
   * Gets the order object from localStorage, creates if it does not exist
   */
  getOrder() {
    // Get order from localStorage
    let order = localStorage.getItem(OrderKey);

    // Creates order object if not exist in localStorage
    if (order === null) {
      const token = localStorage.getItem(Config.AUTH_TOKEN);

      order = {
        customer_id: token ? jwtDecode(token).data.user.id : 0,
        shippingAsBilling: false,
        payment_method_title: '',
        payment_method: '',
        set_paid: false,
        billing: {
          first_name: '',
          last_name: '',
          address_1: '',
          address_2: '',
          city: '',
          state: '',
          postcode: '',
          country: 'US',
          email: '',
          phone: '',
        },
        shipping: {
          first_name: '',
          last_name: '',
          address_1: '',
          address_2: '',
          city: '',
          state: '',
          postcode: '',
          country: '',
        },
        line_items: [],
        shipping_lines: [],
        coupon_lines: [],
      };

      // Set order to localStorage
      this.setOrder(order);
    } else {
      // Parse from localStorage
      order = JSON.parse(order);
    }

    return order;
  }

  /**
   * Sets order to localStorage and to observable
   * @param {object} order
   */
  setOrder(order) {
    localStorage.setItem(OrderKey, JSON.stringify(order));

    this.order = order;
  }

  /**
   * Removes the order from localStorage
   */
  removeOrder() {
    localStorage.removeItem(OrderKey);

    this.order = {};
  }

  /**
   * Returns the order object
   */
  @action async index() {
    return new Promise((resolve) => {
      const order = this.getOrder();

      this.order = order;

      resolve();

      // If there is any coupon we re-apply to the cart
      if (order.coupon_lines.length > 0) this.validateCoupon();

      return order;
    });
  }

  /**
   * Adds an item to the cart => order.line_items
   * @param {object} item
   */
  @action async add(item) {
    return new Promise((resolve) => {
      // Get order
      let order = this.getOrder();
      let emptyCart = false;

      // Checks for product types
      if (order.line_items.length > 0 && item.type === 'subscription') {
        emptyCart = window.confirm(
          'If you select a subscription product you will loose your other cart items! Do you want to proceed?',
        );

        // Empty cart
        if (emptyCart) {
          order.line_items = [];
          order.shipping_lines = [];
        } else {
          return Promise.reject(new Error('stop'));
        }
      } else if (
        _.map(order.line_items, 'type').indexOf('subscription') !== -1
      ) {
        emptyCart = window.confirm(
          'You have selected a subscription product, your cart will be emptied if you buy another product, continue?',
        );

        // Empty cart
        if (emptyCart) {
          order.line_items = [];
          order.shipping_lines = [];
        } else {
          return Promise.reject(new Error('stop'));
        }
      }

      // Validate if product exists in cart
      const existingItem = order.line_items.find(x => x.product_id === item.id);

      // If it does not exist, we can push it to the array
      if (typeof existingItem === 'undefined') {
        order.line_items.push({
          product_id: item.id,
          quantity: 1,
          name: item.name,
          original_price: item.price,
          price: item.price,
          type: item.type,
          slug: item.slug,
          subscription_period:
            _.find(item.meta_data, ['key', '_subscription_period']) || null,
          subscription_length:
            _.find(item.meta_data, ['key', '_subscription_length']) || null,
          image:
            item.images && item.images.length > 0
              ? item.images[0].src
              : 'https://placehold.it/200x150',
          categories:
            item.categories && item.categories.length > 0
              ? item.categories
              : [{ id: 0, name: 'Not defined' }],
        });

        if (
          item.type === 'simple'
          && order.shipping_lines.length < 1
          && !order.free_shipping
        ) {
          order = this.addShipping(order);
        }

        this.addToCartSuccess = true;
      } else {
        // If the product exists we increase the quantity by one
        const productIndex = order.line_items.indexOf(existingItem);
        order.line_items[productIndex].quantity++;

        this.addToCartSuccess = true;
      }

      // Set cart success to false in every case
      setTimeout(() => {
        this.addToCartSuccess = false;
      }, 1500);

      resolve();

      // Set order
      this.setOrder(order);

      // If there is any coupon we re-apply to the cart
      if (order.coupon_lines.length > 0) { this.validateCoupon().catch(() => this.resetCoupon()); }

      return resolve();
    });
  }

  /**
   * Removes an item from the cart => order.line_items
   * Also remove shipping if there is no 'simple' product
   * @param {object} item
   */
  @action async remove(item) {
    return new Promise(() => {
      // Get order
      const order = this.getOrder();

      // Validate if product exists in cart
      const existingItem = order.line_items.find(
        x => x.product_id === item.product_id,
      );

      // If it does not exist we show and alert
      if (typeof existingItem === 'undefined') {
        alert('Product does not exist in cart!');
      } else {
        const productIndex = order.line_items.indexOf(existingItem);
        order.line_items.splice(productIndex, 1);

        if (_.map(order.line_items, 'type').indexOf('simple') === -1) {
          order.shipping_lines = [];
        }
      }

      // Set order
      this.setOrder(order);

      // If there is any coupon we re-apply to the cart
      if (order.coupon_lines.length > 0) { this.validateCoupon().catch(() => this.resetCoupon()); }
    });
  }

  /**
   * Sets the user object upon filling data to localStorage
   * @param {object} item
   */
  @action async setUser(item) {
    return new Promise(() => {
      // Get order
      const order = this.getOrder();

      // Shipping object
      order.shipping = item.shipping;
      // Billing object
      order.billing = item.billing;

      // If billing address is the same shipping (true)
      order.shippingAsBilling = item.shippingAsBilling;
      // If you want to create an account
      order.createAccount = item.createAccount;

      // Set order
      this.setOrder(order);
    });
  }

  /**
   * Sets the customerId on login
   * @param {object} user
   */
  @action async setCustomerId(user) {
    // Get order
    let order = this.getOrder();

    if (order.customer_id !== 0 && order.customer_id !== user.id) {
      this.removeOrder();

      order = this.getOrder();
    }

    order.customer_id = user.id;

    // Set order
    this.setOrder(order);
  }

  /**
   * Increase or decrease quantity for a specific item
   * @param {object} item
   * @param {string} type
   */
  @action async quantity(item, type = '+') {
    // Get order
    const { order } = this;

    // Validate if product exists in cart
    const existingItem = order.line_items.find(
      x => x.product_id === item.product_id,
    );

    // If it does not exist we show and alert
    if (typeof existingItem !== 'undefined') {
      const productIndex = order.line_items.indexOf(existingItem);


      if (type === '+') {
        order.line_items[productIndex].quantity++;
      } else if (order.line_items[productIndex].quantity > 1) {
        order.line_items[productIndex].quantity--;
      }

      // Set order
      this.setOrder(order);

      // If there is any coupon we re-apply to the cart
      this.validateCoupon().catch(() => {
        this.resetCoupon();
      });
    }
  }

  @action shippingZone() {
    if (typeof this.shipping.method_id !== 'undefined') return this.shipping;

    return ApiCore.get('/wc/v3/shipping/zones/1/methods')
      .then((response) => {
        [this.shipping] = response.data;
      }).catch(error => error.response);
  }

  /**
   * Adds simple flat rate shipping to the cart
   * @param {object} order
   */
  addShipping(order) {
    order.shipping_lines = [];

    order.shipping_lines.push({
      method_id: this.shipping.method_id,
      method_title: this.shipping.method_title,
      total: this.shipping.settings.cost.value,
    });

    return order;
  }

  /**
   * Check if logged in user & cart is a match, removes if not
   * @param {string} token
   */
  @action async checkOrder(token) {
    // Get order
    const order = this.getOrder();
    let user = {};

    if (token) {
      ({ user } = jwtDecode(token).data);
    }

    if (
      order.customer_id !== 0
      && order.customer_id !== user.id
      && order !== null
    ) {
      this.removeOrder();
    }
  }

  /**
   * Gets the first coupon with the code, ordered by latest
   * @param {string} couponCode
   */
  @action async couponShow(couponCode) {
    // Get coupon
    const promise = await ApiCore.get('/wc/v3/coupons', { code: couponCode, order: 'desc' }).then(
      (response) => {
        // If we do not find any coupons
        if (response.data.length === 0) { return Promise.reject(new Error('coupon_not_found')); }

        // Switch array to first element
        const coupon = response.data[0];

        this.coupon = coupon;

        return coupon;
      },
    );

    return promise;
  }

  /**
   * Validates if the coupon is valid with the following cases (see comments)
   */
  @action async validateCoupon() {
    const { coupon } = this;
    const { order } = this;
    const total = orderTotal(order, false, 'original_price');

    // Check order product count & remove coupon
    if (order.line_items.length < 1) { return Promise.reject(new Error('coupon_no_line_items')); }

    // Watch expiry date
    if (coupon.date_expires && new Date(coupon.date_expires) < new Date()) { return Promise.reject(new Error('coupon_expired')); }

    // Allowed coupon types
    if (coupon.discount_type !== 'percent') { return Promise.reject(new Error('coupon_invalid')); }

    // Check maximum amount
    if (
      coupon.maximum_amount !== '0.00'
      && parseFloat(coupon.maximum_amount) < total
    ) { return Promise.reject(new Error('coupon_max_exceeded')); }

    // Check minimum amount
    if (
      coupon.minimum_amount !== '0.00'
      && parseFloat(coupon.minimum_amount) > total
    ) { return Promise.reject(new Error('coupon_min_exceeded')); }

    // Handle free shipping
    if (coupon.free_shipping) {
      order.shipping_lines = [];
      order.free_shipping = true;
    }

    // Set coupon code for order
    order.coupon_lines = [{ code: coupon.code }];

    // Handle percentage discounts for each product
    order.line_items.map((item) => {
      item.price = item.original_price * ((100 - parseFloat(coupon.amount)) / 100);
      item.price = item.price.toFixed(2);
    });

    // Set order to localStorage
    this.setOrder(order);

    return Promise.resolve();
  }

  // Reset actionsdone by couponShow
  @action async resetCoupon() {
    let { order } = this;

    // Restore original prices
    order.line_items.map((item) => {
      const lineItem = item;
      lineItem.price = item.original_price;
      return lineItem;
    });

    // Restore shipping
    order.free_shipping = false;
    order = this.addShipping(order);

    // Remove coupons
    order.coupon_lines = [];
    this.coupon = {};

    // Set order to localStorage
    this.setOrder(order);
  }
}

let cartStore = null;

export default function initializeCartStore(initialData) {
  if (isServer) {
    return new CartStore(isServer, initialData);
  }

  if (cartStore === null) {
    cartStore = new CartStore(isServer, initialData);
  }

  return cartStore;
}
